import { Box, Button, Card, CardContent, CircularProgress, Stack, Tab, Tabs, Tooltip } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAppSelector } from '../store'
import { API_BASE_URL } from '../config/api'
import { usePagePermission } from '../hooks/usePagePermission'
import FuturesAllocationListView from './futures/FuturesAllocationListView'
import FuturesBankView from './futures/FuturesBankView'
import FuturesGenericListView from './futures/FuturesGenericListView'
import FuturesLocalPriceDialog from './futures/FuturesLocalPriceDialog'
import FuturesOrdersListView from './futures/FuturesOrdersListView'
import FuturesReportView from './futures/FuturesReportView'
import SpreadsOrdersListView from './futures/SpreadsOrdersListView'
import PageHeader from '../components/PageHeader'

type FuturesTabKey =
  | 'summary'
  | 'margin-report'
  | 'market'
  | 'allocation'
  | 'orders'
  | 'spreads'
  | 'local-p'
  | 'local-s'
  | 'bank-cash-in'
  | 'bank-statement'
  | 'adjust-lot'
  | 'bank-cost-change'
  | 'cash-balance'
  | 'position'
  | 'pnl'
  | 'check-futures'
  | 'reconciliation'

interface FuturesTabConfig {
  key: FuturesTabKey
  label: string
  accessLabel: string
}

const TAB_CONFIG: FuturesTabConfig[] = [
  { key: 'summary', label: 'Summary', accessLabel: 'SUMMARY' },
  { key: 'margin-report', label: 'Margin Report', accessLabel: 'Margin Report' },
  { key: 'market', label: 'Market', accessLabel: 'Market' },
  { key: 'allocation', label: 'Allocation', accessLabel: 'Allocation' },
  { key: 'orders', label: 'Futures Orders', accessLabel: 'Futures' },
  { key: 'spreads', label: 'Spreads Orders', accessLabel: 'Spreads' },
  { key: 'local-p', label: 'Local P', accessLabel: 'Local P' },
  { key: 'local-s', label: 'Local S', accessLabel: 'Local S' },
  { key: 'bank-cash-in', label: 'Bank Cash In', accessLabel: 'Cash-in' },
  { key: 'bank-statement', label: 'Bank Statement', accessLabel: 'Bank Statement' },
  { key: 'adjust-lot', label: 'Adjust Lot', accessLabel: 'Adjustment' },
  { key: 'bank-cost-change', label: 'Bank Cost Change', accessLabel: 'Bank cost change' },
  { key: 'cash-balance', label: 'Cash Balance', accessLabel: 'Cash Balance' },
  { key: 'position', label: 'Position', accessLabel: 'Position' },
  { key: 'pnl', label: 'PnL', accessLabel: 'PnL' },
  { key: 'check-futures', label: 'Check Futures', accessLabel: 'Check Futures' },
  { key: 'reconciliation', label: 'Reconciliation', accessLabel: 'Reconciliation' },
]

const headerButtonSx = {
  minHeight: 24,
  px: 0.75,
  py: 0.15,
  fontSize: '0.67rem',
  lineHeight: 1.1,
  textTransform: 'none' as const,
}

/** Giống Access `btnExportExcel_Click` — `form_frmFuturesMain.txt`. */
const FUTURES_EXPORT_TOKEN = 'JDHK-NDEB-DBCK-DGEJ'

const getDownloadFilename = (disposition: string | null): string | null => {
  if (!disposition) return null

  const filenameStar = /filename\*\s*=\s*([^;]+)/i.exec(disposition)
  if (filenameStar?.[1]) {
    const value = filenameStar[1].trim().replace(/^UTF-8''/i, '').replace(/^"|"$/g, '')
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }

  const filename = /filename\s*=\s*([^;]+)/i.exec(disposition)
  if (!filename?.[1]) return null
  return filename[1].trim().replace(/^"|"$/g, '')
}

function isFuturesTabKey(value: string | undefined): value is FuturesTabKey {
  return TAB_CONFIG.some((tab) => tab.key === value)
}

function renderTab(key: FuturesTabKey) {
  switch (key) {
    case 'orders':
      return <FuturesOrdersListView />
    case 'spreads':
      return <SpreadsOrdersListView />
    case 'allocation':
      return <FuturesAllocationListView />
    case 'adjust-lot':
      return <FuturesGenericListView kind="adjust-lot" />
    case 'bank-cash-in':
      return <FuturesBankView mode="cash-in" />
    case 'bank-statement':
      return <FuturesBankView mode="bank-statement" />
    case 'bank-cost-change':
      return <FuturesGenericListView kind="bank-cost-change" />
    case 'market':
      return <FuturesGenericListView kind="market" />
    case 'local-p':
      return <FuturesGenericListView kind="local-p" />
    case 'local-s':
      return <FuturesGenericListView kind="local-s" />
    case 'margin-report':
      return <FuturesReportView kind="margin-report" />
    case 'summary':
      return <FuturesReportView kind="summary" />
    case 'cash-balance':
      return <FuturesReportView kind="cash-balance" />
    case 'position':
      return <FuturesReportView kind="position" />
    case 'pnl':
      return <FuturesReportView kind="pnl" />
    case 'check-futures':
      return <FuturesReportView kind="check-futures" />
    case 'reconciliation':
      return <FuturesReportView kind="reconciliation" />
    default:
      return <FuturesReportView kind="summary" />
  }
}

export default function FuturesControlPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [localPriceOpen, setLocalPriceOpen] = useState(false)
  const [exporting, setExporting] = useState(false)
  const { canEdit } = usePagePermission()
  const email = useAppSelector((s) => s.auth.user?.email ?? '')

  const handleExportExcel = async () => {
    if (!email) return
    setExporting(true)
    try {
      const url = `${API_BASE_URL}/erp-legacy/download/futures/data?token=${encodeURIComponent(FUTURES_EXPORT_TOKEN)}&email=${encodeURIComponent(email)}`
      const res = await fetch(url)
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const filename = getDownloadFilename(res.headers.get('content-disposition'))
      a.href = href
      a.download = filename ?? 'Futures.xlsx'
      a.click()
      URL.revokeObjectURL(href)
    } finally {
      setExporting(false)
    }
  }

  const activeKey: FuturesTabKey = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean)
    const candidate = segments[1]
    return isFuturesTabKey(candidate) ? candidate : 'summary'
  }, [location.pathname])

  useEffect(() => {
    if (!location.pathname.startsWith('/futures')) return

    const segments = location.pathname.split('/').filter(Boolean)
    const candidate = segments[1]
    if (!candidate || !isFuturesTabKey(candidate)) {
      navigate('/futures/summary', { replace: true })
    }
  }, [location.pathname, navigate])

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Futures Control"
        module="Futures"
        actions={
          <Stack
            direction="row"
            spacing={0.6}
            alignItems="center"
            useFlexGap
            flexWrap="wrap"
            justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
            sx={{ width: '100%' }}
          >
            {canEdit ? (
              <Button size="small" variant="outlined" sx={headerButtonSx} onClick={() => setLocalPriceOpen(true)}>
                Input Local Price
              </Button>
            ) : (
              <Tooltip title="Cần quyền chỉnh sửa màn Futures (tương đương SCREEN_ACCESS = 1 trong Access).">
                <span>
                  <Button size="small" variant="outlined" sx={headerButtonSx} disabled>
                    Input Local Price
                  </Button>
                </span>
              </Tooltip>
            )}
            <Button
              size="small"
              variant="outlined"
              sx={headerButtonSx}
              onClick={handleExportExcel}
              disabled={exporting || !email}
            >
              {exporting ? <CircularProgress size={11} color="inherit" sx={{ mr: 0.5 }} /> : null}
              Export Excel
            </Button>
          </Stack>
        }
      />

      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Tabs
          value={activeKey}
          onChange={(_, value: FuturesTabKey) => {
            navigate(`/futures/${value}`, { replace: false })
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            borderBottom: 1,
            borderColor: 'divider',
            minHeight: 38,
            '& .MuiTabs-indicator': { height: 2 },
            '& .MuiTab-root': {
              minHeight: 38,
              py: 0.25,
              px: 1.5,
              fontSize: '0.8rem',
              fontWeight: 600,
              textTransform: 'none',
            },
          }}
        >
          {TAB_CONFIG.map((tab) => (
            <Tab key={tab.key} label={tab.label} value={tab.key} />
          ))}
        </Tabs>

        <CardContent sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', pt: 2 }}>
          {renderTab(activeKey)}
        </CardContent>
      </Card>

      <FuturesLocalPriceDialog open={localPriceOpen} onClose={() => setLocalPriceOpen(false)} />
    </Box>
  )
}
